/**
 * emit-and-wait.ts — sync request-response pattern over the events table.
 * Emits a request event and waits for a correlated response via Realtime
 * with a poll fallback in case Realtime lags.
 */
import { v4 as uuidv4 } from 'uuid';
import { getSupabase } from '@/auth/supabase-client';
import { emitEvent } from './emit.js';
import type { EventRow } from './emit.js';

export class EmitTimeoutError extends Error {
  constructor(
    public readonly type: string,
    public readonly correlationId: string,
    public readonly timeoutMs: number,
  ) {
    super(`Timeout waiting for ${type} (correlation_id=${correlationId}, ${timeoutMs}ms)`);
    this.name = 'EmitTimeoutError';
  }
}

export interface EmitAndWaitOptions {
  /** Max wait in ms before throwing EmitTimeoutError. Default: 30_000 */
  timeoutMs?: number;
  /** Poll interval ms as fallback if Realtime is slow. Default: 2_000 */
  pollFallbackMs?: number;
}

export async function emitAndWait<
  TRequest extends Record<string, unknown>,
  // TResponse intentionally not constrained to Record<string, unknown> to support
  // interfaces with optional/readonly fields (PreviewResult, CostEstimate, etc.)
  TResponse,
>(
  requestType: string,
  payload: TRequest,
  responseType: string,
  options: EmitAndWaitOptions = {},
): Promise<TResponse> {
  const timeoutMs = options.timeoutMs ?? 30_000;
  const pollFallbackMs = options.pollFallbackMs ?? 2_000;
  const correlationId = uuidv4();
  const sb = getSupabase();

  // Subscribe BEFORE emit to avoid race condition where response arrives
  // before listener is attached.
  const responsePromise = new Promise<TResponse>((resolve, reject) => {
    let settled = false;

    const settle = (fn: () => void): void => {
      if (!settled) {
        settled = true;
        fn();
      }
    };

    const handleRow = (row: EventRow): void => {
      if (row.type !== responseType) return;
      const responsePayload = row.payload as Record<string, unknown>;
      if (typeof responsePayload['error'] === 'string') {
        settle(() => {
          void sb.removeChannel(channel);
          clearInterval(pollInterval);
          clearTimeout(timeoutTimer);
          reject(new Error(responsePayload['error'] as string));
        });
      } else {
        settle(() => {
          void sb.removeChannel(channel);
          clearInterval(pollInterval);
          clearTimeout(timeoutTimer);
          resolve(responsePayload as TResponse);
        });
      }
    };

    const channel = sb
      .channel(`emit-and-wait-${correlationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'events',
          filter: `correlation_id=eq.${correlationId}`,
        },
        (change) => {
          handleRow(change.new as EventRow);
        },
      )
      .subscribe();

    // Poll fallback — handles the case where Realtime misses the event.
    const pollInterval: ReturnType<typeof setInterval> = setInterval(() => {
      void (async () => {
        if (settled) {
          clearInterval(pollInterval);
          return;
        }
        // maybeSingle (not single) — until the worker emits the response
        // row, the query returns 0 rows, and .single() answers with HTTP 406
        // which spams the browser console once per poll tick.
        const { data } = await sb
          .from('events')
          .select('*')
          .eq('correlation_id', correlationId)
          .eq('type', responseType)
          .limit(1)
          .maybeSingle();
        if (data) {
          handleRow(data as EventRow);
        }
      })();
    }, pollFallbackMs);

    const timeoutTimer = setTimeout(() => {
      settle(() => {
        void sb.removeChannel(channel);
        clearInterval(pollInterval);
        reject(new EmitTimeoutError(responseType, correlationId, timeoutMs));
      });
    }, timeoutMs);
  });

  await emitEvent(requestType, payload as Record<string, unknown>, {
    correlation_id: correlationId,
  });

  return responsePromise;
}
